/**
 * Module de traitement des données astrologiques
 */
import { SIGN_START_DEGREE, PLANET_SYMBOLS, PLANET_DISTANCES } from '../core/constants.js';
import { romanToArabic } from '../core/utils.js';

export class DataProcessor {
    /**
     * Prépare les données pour le rendu
     * @param {Object} astrologyChart - Données brutes du thème astral
     * @returns {Object} Données structurées pour le rendu
     */
    static prepareData(astrologyChart) {
        return {
            houses: this._processHouses(astrologyChart),
            planets: this._processPlanets(astrologyChart),
            aspects: this._processAspects(astrologyChart)
        };
    }

    /**
     * Convertit les cusps des maisons en degrés absolus (0-360°)
     * @private
     */
    static _processHouses(astrologyChart) {
        const houses = [];

        if (astrologyChart.houseCusps && astrologyChart.houseCusps.length > 0) {
            astrologyChart.houseCusps.forEach(cusp => {
                const houseNum = romanToArabic(cusp.house);
                const signDeg = SIGN_START_DEGREE[cusp.sign] || 0;
                const absoluteDeg = (signDeg + cusp.degree) % 360;

                houses[houseNum - 1] = {
                    number: houseNum,
                    position: absoluteDeg,
                    signName: cusp.sign
                };
            });
        }

        return houses;
    }

    /**
     * Traite les données des planètes
     * @private
     */
    static _processPlanets(astrologyChart) {
        const planets = [];

        if (astrologyChart.placements) {
            astrologyChart.placements.forEach(placement => {
                // Ne garder que les planètes principales
                if (PLANET_SYMBOLS[placement.point]) {
                    planets.push({
                        name: placement.point,
                        symbol: PLANET_SYMBOLS[placement.point],
                        position: placement.eclipticLongitude,
                        // Le moteur arcate expose `speed` (deg/j) plutôt qu'un booléen retrograde :
                        // vitesse écliptique négative = mouvement rétrograde.
                        retrograde: placement.retrograde ?? (placement.speed < 0),
                        distance: PLANET_DISTANCES[placement.point] || 0.5 // Distance par défaut si non spécifiée
                    });
                }
            });
        }

        return planets;
    }

    /**
     * Traite les aspects entre planètes
     * @private
     */
    static _processAspects(astrologyChart) {
        const aspects = [];

        if (astrologyChart.aspects) {
            astrologyChart.aspects.forEach(aspect => {
                // Ne garder que les aspects entre planètes principales
                if (PLANET_SYMBOLS[aspect.point1] && PLANET_SYMBOLS[aspect.point2]) {
                    aspects.push({
                        point1: aspect.point1,
                        point2: aspect.point2,
                        type: aspect.type,
                        orb: aspect.orb,
                        effectiveStrength: aspect.effectiveStrength
                    });
                }
            });
        }

        return aspects;
    }

    /**
     * Filtre les aspects pour ne garder que les plus importants
     * @param {Array} aspects - Liste des aspects
     * @returns {Array} Liste filtrée des aspects
     */
    static filterImportantAspects(aspects) {
        // Aspects majeurs à conserver
        const majorAspects = ['CONJUNCTION', 'OPPOSITION', 'TRINE', 'SQUARE', 'SEXTILE'];

        // Seuil de force pour les aspects mineurs
        const strengthThreshold = 0.25;

        return aspects.filter(aspect => {
            // Toujours garder les aspects majeurs
            if (majorAspects.includes(aspect.type)) {
                return true;
            }

            // Pour les aspects mineurs, ne garder que ceux avec une force significative
            return Math.abs(aspect.effectiveStrength) >= strengthThreshold;
        });
    }
}